import React, { ReactNode } from "react";
import {
  Box,
  Circle,
  Flex,
  HStack,
  Img,
  ImgProps,
  useColorModeValue,
} from "@chakra-ui/react";

type CloudinaryProps = {
  src: string;
  alt: string;
};

export const Cloudinary = ({
  src,
  alt,
  ...props
}: CloudinaryProps & ImgProps) => (
  <Img
    alt={alt}
    src={
      "https://res.cloudinary.com/apollographql/image/upload/e_sharpen:50,c_scale,q_90,w_1440,fl_progressive/" +
      src
    }
    {...props}
  />
);

export function CloudinaryGif({ src, alt }: CloudinaryProps) {
  return (
    <video
      autoPlay
      loop
      muted
      playsInline
      src={`https://res.cloudinary.com/apollographql/video/upload/${src}`}
      title={alt}
    />
  );
}

type RoundCornerImageProps = {
  children: ReactNode;
  bgColor?: string;
  borderColor?: string;
};

type ScreenshotProps = {
  url?: string;
  children: ReactNode;
};

type ScreenshotWindowButtonsProps = {
  colors: string[];
};

const ScreenshotWindowButtons = ({ colors }: ScreenshotWindowButtonsProps) => (
  <HStack spacing="1.5">
    {colors.map((color, index) => (
      <Circle key={index} size="3" borderRadius="full" bgColor={color} />
    ))}
  </HStack>
);

export function Screenshot({ url, children }: ScreenshotProps) {
  const windowBg = useColorModeValue("gray.100", "indigo.500");
  const urlBg = useColorModeValue("silver.400", "indigo.400");

  return (
    <RoundCornerImage>
      <Flex
        justify="space-between"
        py={url ? 1.5 : 2}
        px={url ? 4 : 2.5}
        bg={windowBg}
      >
        <ScreenshotWindowButtons
          colors={["orange.300", "green.400", "blue.300"]}
        />

        {url && (
          <>
            <Box
              w="full"
              maxW={480}
              mx="6"
              borderWidth={1}
              borderRadius="md"
              bg={urlBg}
              px="4"
              lineHeight="taller"
              fontSize="sm"
              textAlign="center"
            >
              {url}
            </Box>
            <ScreenshotWindowButtons
              colors={["transparent", "transparent", "transparent"]}
            />
          </>
        )}
      </Flex>
      {children}
    </RoundCornerImage>
  );
}

export function RoundCornerImage({
  bgColor,
  borderColor,
  children,
}: RoundCornerImageProps) {
  const bg = useColorModeValue("gray.100", "whiteAlpha.200");
  const urlBg = useColorModeValue("silver.400", "indigo.400");

  return (
    <Box
      borderRadius="lg"
      shadow="md"
      borderWidth={1}
      borderColor={borderColor || urlBg}
      overflow="hidden"
      bg={bgColor || bg}
    >
      {children}
    </Box>
  );
}
